import { ModalBaseProps } from '@round/ui-kit';
import { FullscreenModal } from '@round/whitelabel-ui';
import UserAudienceBreakdown from 'Modules/Plans/User/UserModal/components/Audience/UserAudienceBreakdown';
import HeadlineStats from 'Modules/Plans/User/UserModal/components/HeadlineStats/HeadlineStats';
import AccountDetail, { AccountDetailProps } from 'Modules/Plans/User/UserModal/components/AccountDetail/AccountDetail';
import { useInstagramUser, useInstagramUserImages, useInstagramUserStatsCache } from '@round/platforms/instagram';
import { useInstagramUserAnalytics } from 'Modules/Influencer/hooks/queries/useInstagramUserAnalytics';

type Props = Pick<ModalBaseProps, 'isOpen' | 'onClose'> & {
    userId: number | null;
};

export default function InstagramUserModal({ isOpen, onClose, userId }: Props) {
    const analytics = useInstagramUserAnalytics(userId, { select: (data) => data.data });
    const statsCache = useInstagramUserStatsCache(
        { user_id: typeof userId === 'number' ? [userId] : [] },
        { enabled: typeof userId === 'number' }
    );
    const statsCacheRow = statsCache.data?.results.find((s) => s.user_id === userId);

    const instagramUser = useInstagramUser(userId);
    const userImages = useInstagramUserImages(typeof userId === 'number' ? [userId] : [], {
        enabled: typeof userId === 'number',
    });
    const userImage = userImages.data?.find((img) => img.user_id === userId);

    const accountDetailProps: AccountDetailProps = {
        platform: 'instagram',
        isAccountDataLoading: instagramUser.isLoading || userImages.isLoading,
        isUserStatsCacheLoading: statsCache.isLoading,
        userImage: userImage?.avatar_thumb?.cached_url || userImage?.avatar_thumb?.original_url,
        userHandle: instagramUser.data?.username,
        username: instagramUser.data?.name,
        bio: instagramUser.data?.bio,
        location: statsCacheRow?.location,
        onClose: onClose,
    };

    return (
        <FullscreenModal isOpen={isOpen} onClose={onClose} closeOnOverlayClick closeOnEscape>
            <AccountDetail {...accountDetailProps} />
            <HeadlineStats data={statsCacheRow} isLoading={statsCache.isLoading} />
            <UserAudienceBreakdown
                data={analytics.data}
                isLoading={analytics.isLoading}
                errorMessage={analytics.errorMessage}
            />
        </FullscreenModal>
    );
}
